import {
  ContentLayout,
  ServerPaginatedTable,
  useDropdownApis,
  AutocompleteWithEndAdornmentChip,
  LimitedTag,
} from "../../../shared";
import * as React from "react";
import {
  Button,
  Checkbox,
  createFilterOptions,
  Stack,
  TextField,
  ListItemButton,
  Typography,
  ListItemText,
} from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import InputControl from "../../../shared/components/InputControl";
import CloseIcon from "@mui/icons-material/Close";
import { DashBoardDateRangePicker } from "../../Dashboard";
import { useWastageList } from "../hooks";
import { arraySort } from "../../../utils";

export const WastageList = () => {
  const { pathname } = useLocation();
  const isStoreAdmin = pathname.includes("store-admin");
  const {
    state,
    wastageType,
    onChangeFilter,
    onSeachClear,
    resetFilters,
    gotoPage,
    setPageSize,
    handleDownloadWastageList,
    handleSort,
    onChangeDate,
    handleIsOpen,
    handleDateChangeApply,
    handleApplyFilter,
    handleFilterClear,
  } = useWastageList({ isWastage: true });
  const { storeList } = useDropdownApis({
    isStoreList: true,
  });
  const filterOptions = createFilterOptions({
    matchFrom: "any",
    stringify: (option) => option.label,
  });

  const storeOptions = storeList?.map((v) => {
    return { value: v.id, label: v.name };
  });

  const ActionButtons = ({ id }) => {
    return (
      <div className="action-buttons">
        <Link to={`details/${id}`}>
          <img alt="details" src="/icons/arrow_forward.svg" />
        </Link>
      </div>
    );
  };

  const columns = React.useMemo(
    () => [
      {
        Header: "Wastage Number",
        accessor: "wastage_number",
      },
      {
        Header: "Date & Time",
        accessor: "wastage_date",
      },
      {
        Header: "Wastage Type",
        accessor: "wastage_type",
        id: "wastage_type_name",
      },
      {
        Header: "Store",
        accessor: "store_name",
      },
      {
        Header: "Total Items",
        accessor: "total_products",
      },
      {
        Header: "Grand Total",
        accessor: "grant_total",
        Cell: ({ value }) => {
          return <div>{value?.toFixed(2) || 0}</div>;
        },
      },
      {
        Header: "Staff Name",
        accessor: "user_name",
      },
      {
        Header: "Action",
        id: "action",
        align: "right",
        disableSortBy: true,
        accessor: (row, index) => {
          return <ActionButtons id={row.id} />;
        },
      },
    ],
    []
  );

  const tablecolumns = isStoreAdmin
    ? columns.filter((v) => v.Header !== "Store")
    : columns;

  return (
    <>
      <ContentLayout
        title="Wastage"
        rightContent={
          <>
            <Button
              type="button"
              size="medium"
              variant="text"
              onClick={handleDownloadWastageList}
            >
              <img
                alt="export"
                src="/icons/ic_export.svg"
                style={{
                  marginRight: "4px",
                }}
              />
              Export
            </Button>
          </>
        }
      >
        <ServerPaginatedTable
          columns={tablecolumns}
          data={state.wastage}
          key={state.sortBy}
          isLoading={state?.isBusy}
          defaultSearch={false}
          gotoPage={gotoPage}
          setPageSize={setPageSize}
          pagination={state.pagination}
          handleSort={handleSort}
          sortByGlobal={state.sortBy}
          filterApplied={state.filters.all}
          onFilterClear={(e) => handleFilterClear("single", e)}
          onAllFilterClear={(e) => handleFilterClear("all")}
          filterContent={
            <InputControl
              type="table-search"
              name="searchText"
              placeholder="Search"
              size="small"
              value={state.filters.searchText}
              onChange={(e, v) => onChangeFilter(e, v, "searchText")}
              onClear={onSeachClear}
            />
          }
          filterSection={
            <>
              <Stack direction="row" gap="16px" alignItems="center">
                <DashBoardDateRangePicker
                  onChangeDate={onChangeDate}
                  isList={true}
                  range={state.date}
                  showRange={state.filters.date}
                  isOpen={state.isCalenderOpen}
                  handleIsOpen={handleIsOpen}
                  handleDateChangeApply={handleDateChangeApply}
                />
                <AutocompleteWithEndAdornmentChip
                 name="wastageType"
                  label="Wastage Type"
                  options={arraySort(wastageType || [], "label")}
                  value={state.filters.wastageType || []}
                  handleChangeFilter={({ name, value }) => {
                    onChangeFilter(
                      { target: { name: "wastage", value: value } },
                      value,
                      "wastage"
                    );
                  }}
                  limitedTag={(value) => (
                    <LimitedTag
                      labelKey="label"
                      value={value}
                      autocompleteWidth="230px"
                      labelWidth="110px"
                    />
                  )}
                  groupBy={() => "All"}
                  renderGroup={(params) => {
                    const isAllChecked =
                      (state.filters.wastage || []).length ===
                      wastageType.length;
                    return (
                      <React.Fragment key={params.group}>
                        {params.key === 0 && (
                          <ListItemButton
                            disableGutters
                            onClick={() =>
                              onChangeFilter(
                                {
                                  target: {
                                    name: "wastage",
                                    value: isAllChecked ? [] : wastageType,
                                  },
                                },
                                isAllChecked ? [] : wastageType,
                                "wastage"
                              )
                            }
                            selected={isAllChecked}
                          >
                            <Checkbox size="small" checked={isAllChecked} />
                            <Typography>All</Typography>
                          </ListItemButton>
                        )}
                        <ListItemText>{params.children}</ListItemText>
                      </React.Fragment>
                    );
                  }}
                  sx={{ minWidth: 230, maxWidth: "fit-content" }}
                />
                {!isStoreAdmin && (
                  <>
                    <AutocompleteWithEndAdornmentChip
                      name="stores"
                      label="Store"
                      options={arraySort(storeOptions || [], "label")}
                      value={state.filters.stores || []}
                      handleChangeFilter={({ name, value }) => {
                        onChangeFilter(
                          { target: { name: "store", value: value } },
                          value,
                          "store"
                        );
                      }}
                      limitedTag={(value) => (
                        <LimitedTag
                          labelKey="label"
                          value={value}
                          autocompleteWidth="230px"
                          labelWidth="110px"
                        />
                      )}
                      groupBy={() => "All"}
                      renderGroup={(params) => {
                        const isAllChecked =
                          (state.filters.stores || []).length ===
                          storeOptions.length;
                        return (
                          <React.Fragment key={params.group}>
                            {params.key === 0 && (
                              <ListItemButton
                                disableGutters
                                onClick={() =>
                                  onChangeFilter(
                                    {
                                      target: {
                                        name: "store",
                                        value: isAllChecked ? [] : storeOptions,
                                      },
                                    },
                                    isAllChecked ? [] : storeOptions,
                                    "store"
                                  )
                                }
                                selected={isAllChecked}
                              >
                                <Checkbox size="small" checked={isAllChecked} />
                                <Typography>All</Typography>
                              </ListItemButton>
                            )}
                            <ListItemText>{params.children}</ListItemText>
                          </React.Fragment>
                        );
                      }}
                      sx={{ minWidth: 230, maxWidth: "fit-content" }}
                    />
                  </>
                )}
              </Stack>
              <Button
                type="button"
                size="medium"
                variant="text"
                color="info"
                sx={{
                  minWidth: "40px",
                  p: "8px",
                }}
                onClick={() => handleApplyFilter()}
              >
                Apply
              </Button>
            </>
          }
          onRowLink={(e) => {
            return `details/${e.id}`;
          }}
        />
      </ContentLayout>
    </>
  );
};
